import React, { createContext, useContext, useState } from "react";
import {
    PublicClientApplication,
} from "@azure/msal-browser";
import { Client } from "@microsoft/microsoft-graph-client";
import apis from "../utils/apis";
import { translate } from '../i18n/translate';

const CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || "";
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI || window.location.origin;

const msalConfig = {
    auth: {
        clientId: CLIENT_ID,
        redirectUri: REDIRECT_URI,
    },
};

export const msalInstance = new PublicClientApplication(msalConfig);

interface MicrosoftGraphContextProps {
    accessToken: string | null;
    isSignedIn: boolean;
    MSALsignIn: () => Promise<any>;
    signOut: () => void;
    uploadFileToOneDrive: (fileName: string, fileContent: Blob, SharedWith: any[]) => Promise<any>;
    getEditLink: (fileId: string) => Promise<string>;
    downloadFileFromOneDrive: (fileId: string) => Promise<Blob>;
    deleteFileFromOneDrive: (fileId: string) => Promise<void>;
    restrictSharing: (fileId: string) => Promise<void>;
    fetchCurrentUserDetails: () => Promise<any>;
    handleOpenMicrosoftPicker: (file: any) => Promise<void>;
    unlockFileOnOneDrive: (fileId: string) => Promise<void>;
}

const defaultContext: MicrosoftGraphContextProps = {
    accessToken: null,
    isSignedIn: false,
    MSALsignIn: async () => { },
    signOut: () => { },
    uploadFileToOneDrive: async () => { },
    getEditLink: async () => "",
    downloadFileFromOneDrive: async () => new Blob(),
    deleteFileFromOneDrive: async () => { },
    restrictSharing: async () => { },
    fetchCurrentUserDetails: async () => { },
    handleOpenMicrosoftPicker: async () => { },
    unlockFileOnOneDrive: async () => { },
};
let localAccessToken: any = null
const MicrosoftGraphContext = createContext<MicrosoftGraphContextProps>(defaultContext);

export const MicrosoftGraphProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [isSignedIn, setIsSignedIn] = useState(false);
    const getBackendToken = async () => {
        try{

            if (accessToken) return;                      
            const response = await apis.get<{ access_token: string }>(
                "/integrations/auth/tokens/onedrive",
            )
            
            if (response.status === 200 && response.data.access_token) {
                const Token = response.data.access_token
                setAccessToken(Token);
                setIsSignedIn(true)
                return Token
            } else {
                return null
            }
        }catch(e){
            console.warn('[OneDrive] Not connected - redirect to integrations settings');
            window.location.href = '/settings?tab=integrations';
            return null;
        }
    };

    const MSALsignIn = async () => {
        // Redirect to integrations settings to connect OneDrive
        window.location.href = '/settings?tab=integrations';
    };

    const signOut = () => {
    };


    const getGraphClient = async () => {
        if (!accessToken) {
            return Client.init({
                authProvider: (done: any) => {
                    done(null, localAccessToken);
                },
            });
        } else {
            return Client.init({
                authProvider: (done: any) => {
                    done(null, accessToken);
                },
            });

        }
    };

    const uploadFileToOneDrive = async (fileName: string, fileContent: Blob, SharedWith: any[]): Promise<any> => {
        try {
            const client = await getGraphClient();
            let updatedSharedWith = null

            try {
                const response = await client.api(`/me/drive/root:/${fileName}`).get();
                if (response && response.id) {
                    return { fileID: response.id, updatedSharedWith: updatedSharedWith, webURL: response.webUrl };
                }
            } catch (error: any) {
                if (error.statusCode === 404) {
                    const response = await client
                        .api(`/me/drive/root:/${fileName}:/content?@microsoft.graph.conflictBehavior=replace`)
                        .put(fileContent);

                    if (!response || !response.id) {
                        throw new Error("Failed to upload file: No response or missing file ID.");
                    }
                    const mappedEmails = SharedWith.map(({ Email, Permission }) => ({
                        email: Email,
                        roles: [Permission]
                    })) || [];
                    if (mappedEmails.length > 0) {
                        const { sharedLinks } = await shareAccess(response.id, mappedEmails)

                        updatedSharedWith = SharedWith.map(user => {
                            const sharedLinkEntry = sharedLinks.find(link => link.email === user.Email);
                            return {
                                ...user,
                                SharedURL: sharedLinkEntry ? sharedLinkEntry.link : null, // Add link if found
                            };
                        });

                    }

                    return { fileID: response.id, updatedSharedWith: updatedSharedWith, webURL: null };
                } else {
                    throw error;
                }
            }
        } catch (error) {
            throw error;
        }
    };

    const getEditLink = async (fileId: string): Promise<string> => {
        const client = await getGraphClient();
        const response = await client.api(`/me/drive/items/${fileId}/createLink`).post({
            type: 'edit',
            scope: 'anonymous'
        });
        if (!response) {
            const error = await response;
            throw new Error(`Error creating link: ${error.error}`);
        }

        const data = await response;

        return data.link.webUrl;
    };

    const downloadFileFromOneDrive = async (fileId: string): Promise<Blob> => {
        try {
            const fileContent = await fetchFileContent(fileId, accessToken)
            return fileContent;
        } catch (error) {
            throw error;
        }
    };

    const deleteFileFromOneDrive = async (fileId: string) => {
        try {
            const url = `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}`;
            const response = await fetch(url, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({})
            });

            if (response.status === 204) {
            } else {
                const errorDetails = await response.json();
                throw new Error(errorDetails.error.message);
            }
        } catch (error) {
            throw error;
        }
    };
    const restrictSharing = async (fileId: string): Promise<void> => {
        const client = await getGraphClient();
        const permissions = await client.api(`/me/drive/items/${fileId}/permissions`).get();

        for (const permission of permissions.value) {
            await client.api(`/me/drive/items/${fileId}/permissions/${permission.id}`).delete();
        }

    };

    const fetchCurrentUserDetails = async (): Promise<any> => {
        const client = await getGraphClient();
        const user = await client.api('/me').get();
        return user;
    };

    const fetchFileContent = async (fileId: string, token: any): Promise<Blob> => {
        try {
            const response = await fetch(
                `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/content`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken ? accessToken : token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch file content: ${response.statusText}`);
            }

            const blob = await response.blob();
            return blob;
        } catch (error) {
            throw error;
        }
    };




    const handleOpenMicrosoftPicker = async (onFileSelected: any) => {
        try {
            const activeToken = await getBackendToken()
            const odOptions = {
                clientId: CLIENT_ID,
                action: "query",
                multiSelect: true,
                advanced: {
                    accessToken: activeToken,
                    queryParameters: "select=id,name,size,file,folder,photo,@microsoft.graph.downloadUrl,webUrl",
                    filter: "folder,.docx",
                    createLinkParameters: { type: "edit", scope: "organization" },
                },
                success: async (files: any) => {
                    try {
                        const selectedFiles = files.value || [];
                        if (!selectedFiles.length) {
                            return;
                        }

                        const fileObjects = await Promise.all(
                            selectedFiles.map(async (file: any) => {
                                const content = await fetchFileContent(file.id, activeToken);
                                return new File([content], file.name, {
                                    type: file.file?.mimeType || "application/octet-stream",
                                    lastModified: Date.now(),
                                });
                            })
                        );


                        onFileSelected([...fileObjects]); 
                    } catch (fetchError) {
                        alert("Error retrieving one or more files. Please try again.");
                    }
                },
                cancel: () => {
                },
                error: (pickerError: any) => {
                },
            };

            OneDrive.open(odOptions);
        } catch (generalError) {
        }
    };

    const unlockFileOnOneDrive = async (fileId: string) => {
        const url = `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/unlock`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({})
        });

        if (!response.ok) {
            throw new Error(`Failed to unlock file: ${response.statusText}`);
        }

    };

    const shareAccess = async (
        fileID: string,
        emails: { email: string; roles: ("write" | "read" | "delete" | "rename")[] }[]
    ) => {
        try {
            const client = await getGraphClient();

            const roleMappings: Record<string, string[]> = {
                write: ["write"], 
                read: ["read"], 
                delete: ["write"], 
                rename: ["write"],
            };

            let sharedUsersWithLinks: { email: string; link: string }[] = [];

            for (const { email, roles } of emails) {
                let graphRoles: string[] = [];

                roles.forEach((role) => {
                    if (roleMappings[role]) {
                        graphRoles = [...new Set([...graphRoles, ...roleMappings[role]])];
                    }
                });

                await client.api(`/me/drive/items/${fileID}/invite`).post({
                    requireSignIn: true,
                    sendInvitation: true,
                    roles: graphRoles,
                    recipients: [{ email }],
                });

                const linkType = roles.includes("write") ? "edit" : "view"; 
                const linkResponse = await client.api(`/me/drive/items/${fileID}/createLink`).post({
                    type: linkType, 
                    scope: "anonymous",
                });

                if (linkResponse?.link?.webUrl) {
                    sharedUsersWithLinks.push({ email, link: linkResponse.link.webUrl });
                }
            }

            return {
                message: translate("File sharing completed successfully!"),
                sharedLinks: sharedUsersWithLinks,
            };
        } catch (error) {
            throw error;
        }
    };



    return (
        <MicrosoftGraphContext.Provider
            value={{
                accessToken,
                isSignedIn,
                MSALsignIn,
                signOut,
                uploadFileToOneDrive,
                getEditLink,
                downloadFileFromOneDrive,
                deleteFileFromOneDrive,
                restrictSharing,
                fetchCurrentUserDetails,
                handleOpenMicrosoftPicker,
                unlockFileOnOneDrive
            }}
        >
            {children}
        </MicrosoftGraphContext.Provider>
    );
};

export const useMicrosoftGraph = () => useContext(MicrosoftGraphContext);
